import React, { useEffect } from "react";

import {
    WindowLocation,
    WindowLocationAction,
    WindowLocationActionTypes
} from "interfaces/context/searchContextInterfaces";
import { useSearchState } from "./SearchContext";

type WindowLocationDispatch = (action: WindowLocationAction) => void;

const defaultWindowLocationState = {
    hash: "",
    host: "",
    hostname: "",
    href: "",
    origin: "",
    pathname: "",
    port: "",
    protocol: "",
    search: ""
};

const windowLocationReducer = (state: WindowLocation, action: WindowLocationAction) => {
    switch (action.type) {
    case WindowLocationActionTypes.updateWindowLocation: {
        const {
            hash,
            host,
            hostname,
            href,
            origin,
            pathname,
            port,
            protocol,
            search
        } = window?.location;

        return {
            ...state,
            ...{
                hash,
                host,
                hostname,
                href,
                origin,
                pathname,
                port,
                protocol,
                search
            }
        };
    }
    default: {
        throw new Error(`Unhandled action: ${action}`);
    }
    }
};

const WindowLocationContext = React.createContext<WindowLocation | null>(defaultWindowLocationState);
const WindowLocationDispatchContext = React.createContext<WindowLocationDispatch | undefined>(undefined);

const WindowLocationProvider: React.FC = ({ children }) => {
    const [state, dispatch] = React.useReducer(windowLocationReducer, defaultWindowLocationState);
    const { url } = useSearchState();

    useEffect(() => {
        dispatch({
            type: WindowLocationActionTypes.updateWindowLocation,
            payload: window.location
        });
    }, [url]);

    return (
        <WindowLocationContext.Provider value={state}>
            <WindowLocationDispatchContext.Provider value={dispatch as WindowLocationDispatch}>
                {children}
            </WindowLocationDispatchContext.Provider>
        </WindowLocationContext.Provider>
    );
};

const useWindowLocationState = () => {
    const context = React.useContext(WindowLocationContext);
    if (context === undefined) {
        throw new Error("useWindowLocationState must be used within the WindowLocationProvider");
    }
    return context;
};

const useWindowLocationDispatch = () => {
    const context = React.useContext(WindowLocationDispatchContext);
    if (context === undefined) {
        throw new Error("useWindowLocationDispatch must be used within the WindowLocationProvider");
    }
    return context;
};

export { WindowLocationProvider, useWindowLocationState, useWindowLocationDispatch };
